<template>
  <div>
    <div class="pb-2">
      <h1 class="title title--h1 first-title title__separate">Resume</h1>
    </div>

    <div class="row">
      <div class="col">
        <h2 class="title title--h3">
          <img :src="require('@/assets/images/icon-experience.ec6a56a4.svg')" alt="experience" class="title-icon"> Experience
        </h2>
        <div class="timeline">
          <article class="timeline__item" v-for="exp in experiences" :key="exp.title + exp.period">
            <h5 class="title title--h5 timeline__title">
              {{ exp.company }} <small> as </small> {{ exp.role }}
            </h5>
            <strong>{{ exp.location }}</strong><br>
            <span class="timeline__period">{{ exp.period }}</span>
            <p class="timeline__description">
              <span class="pre-wrap" v-html="exp.description"></span>
            </p>
          </article>
        </div>
      </div>
    </div>

    <div class="row mt-4">
      <div class="col-sm">
        <h2 class="title title--h3">
          <img :src="require('@/assets/images/icon-education.cc7f0741.svg')" alt="education" class="title-icon"> Education
        </h2>
        <div class="timeline">
          <article class="timeline__item" v-for="edu in education" :key="edu.school">
            <h5 class="title title--h5 timeline__title">{{ edu.school }}</h5>
            <span class="timeline__period">{{ edu.period }}</span>
            <p class="timeline__description">
              <span v-if="edu.description" class="pre-wrap">{{ edu.description }} <br></span>
              {{ edu.location }}
            </p>
          </article>
        </div>
      </div>
      <div class="col-sm">
        <h2 class="title title--h3">
          <img :src="require('@/assets/images/icon-education.cc7f0741.svg')" alt="education" class="title-icon"> Nonformal Education
        </h2>
        <div class="timeline">
          <article class="timeline__item" v-for="nf in nonformalEducation" :key="nf.school + nf.period">
            <h5 class="title title--h5 timeline__title">{{ nf.school }}</h5>
            <span class="timeline__period">{{ nf.period }}</span>
            <p class="timeline__description">
              <span class="pre-wrap">{{ nf.description }} <br></span>
              {{ nf.location }}
            </p>
          </article>
        </div>
      </div>
    </div>

    <div class="row mt-4">
      <div class="col-12">
        <h2 class="title title--h3">Skills</h2>
        <div class="box box__border">
          <div>
            <div class="progress" v-for="skill in skills" :key="skill.name">
              <div role="progressbar" aria-valuemin="0" aria-valuemax="100" class="progress-bar" :style="{ width: skillWidth(skill.level), zIndex: 2 }">
                <div class="progress-text">
                  <span>{{ skill.name }}</span>
                  <span>{{ skill.level }}</span>
                </div>
              </div>
              <div class="progress-text">
                <span>{{ skill.name }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="row mt-4">
      <div class="col-12">
        <h2 class="title title--h3">Code Skills</h2>
        <div class="box box__border">
          <div>
            <div class="progress" v-for="skill in codeSkills" :key="skill.name">
              <div role="progressbar" aria-valuemin="0" aria-valuemax="100" class="progress-bar" :style="{ width: skillWidth(skill.level), zIndex: 2 }">
                <div class="progress-text">
                  <span>{{ skill.name }}</span>
                  <span>{{ skill.level }}</span>
                </div>
              </div>
              <div class="progress-text">
                <span>{{ skill.name }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ResumeView',
  data() {
    return {
      experiences: [
        {
          company: 'Amdocs',
          role: 'DevOps Expert / Software Technical Expert',
          location: 'Limassol, Cyprus',
          period: 'Jun 2024 - Present',
          description: `- Leading DevOps transformation for R&D division across hybrid cloud environments
- Transformed CI/CD architecture (AWS CodePipeline, Jenkins, ArgoCD) achieving 75% faster releases and 40% fewer deployment errors
- Unified IaC platform with Terraform across 50+ environments resulting in 4x faster provisioning and 30% fewer incidents
- Led container migration of 90% legacy apps to Kubernetes achieving 50% better scalability and $150K annual savings
- Championed DevSecOps (SAST/DAST) and mentored 10+ engineers resulting in 35% fewer vulnerabilities
- Cloud cost optimization via reserved instances and right-sizing achieving 20-35% cost reduction <br>`
        },
        {
          company: 'Amdocs',
          role: 'DevOps Specialist',
          location: 'Limassol, Cyprus',
          period: 'Feb 2021 - Jun 2024',
          description: `- Promoted to lead CI/CD architecture and cloud migration for business unit
- Pioneered Kubernetes-native CI/CD on OpenShift achieving 60% faster build times
- Integrated automated testing into pipelines resulting in 45% reduction in failed deployments
- Led AWS migration for legacy infrastructure achieving 40% lower operational overhead and 3x scalability
- Delivered customer cloud projects with standardized runbooks enabling 25% faster deployments <br>`
        },
        {
          company: 'Amdocs',
          role: 'DevOps Engineer',
          location: 'Limassol, Cyprus',
          period: 'May 2018 - Feb 2021',
          description: `- Promoted to focus on AWS infrastructure automation and SRE practices
- Automated infrastructure provisioning (Terraform & Ansible) reducing manual tasks by 50%
- Managed containerized microservices (Docker/Kubernetes/Helm) achieving 40% faster deployments
- Architected secure AWS networking (VPN, VPC peering, ALB, WAF) with 30% better throughput and zero security incidents
- Automated Linux administration via shell scripting reducing manual intervention by 60% <br>`
        },
        {
          company: 'Amdocs India',
          role: 'Technology Integration Engineer',
          location: 'Pune, India',
          period: 'May 2013 - May 2018',
          description: `- Built enterprise integration solutions for telecom systems and collaborated with global teams
- Managed 100+ Linux production servers for AT&T enterprise infrastructure (10M+ subscribers)
- Achieved 99.95% availability through proactive monitoring and maintenance
- Promoted 3 times for consistent performance and technical leadership <br>`
        },
        {
          company: 'NanoStuffs Technology Pvt Ltd',
          role: 'Mobile Apps Developer',
          location: 'Pune, India',
          period: 'Mar 2012 - Apr 2013',
          description: `- Developed Android applications using Java and built programming foundation for DevOps tooling
- Designed and developed 5+ mobile applications from concept to deployment <br>`
        }
      ],
      education: [
        {
          school: 'Sant Gadge Baba Amravati University (SGBAU)',
          period: '2007 - 2011',
          description: 'Bachelor of Engineering in Information Technology',
          location: 'Maharashtra, India'
        }
      ],
      nonformalEducation: [
        {
          school: 'Cloud Native Computing Foundation (CNCF)',
          period: 'Certified',
          description: 'CKA - Certified Kubernetes Administrator',
          location: 'Professional Certification'
        },
        {
          school: 'Cloud Native Computing Foundation (CNCF)',
          period: 'Certified',
          description: 'CKAD - Certified Kubernetes Application Developer',
          location: 'Professional Certification'
        },
        {
          school: 'Amazon Web Services',
          period: 'Certified',
          description: 'AWS Certified Cloud Practitioner',
          location: 'Professional Certification'
        },
        {
          school: 'DevOps Institute',
          period: 'Certified',
          description: 'CP-DOF - Certified Professional DevOps Foundation',
          location: 'Professional Certification'
        }
      ],
      skills: [
        { name: 'AWS (EC2, EKS, S3, RDS, Lambda, IAM)', level: 'Expert' },
        { name: 'Kubernetes & OpenShift', level: 'Expert' },
        { name: 'CI/CD & GitOps', level: 'Expert' },
        { name: 'Infrastructure as Code (Terraform)', level: 'Expert' },
        { name: 'Linux Administration', level: 'Expert' },
        { name: 'Observability & SRE', level: 'Advanced' },
        { name: 'DevSecOps & Compliance', level: 'Advanced' },
        { name: 'Agile & Scrum', level: 'Advanced' }
      ],
      codeSkills: [
        { name: 'Docker & Containers', level: 'Expert' },
        { name: 'Kubernetes & Helm', level: 'Expert' },
        { name: 'Terraform & Ansible', level: 'Expert' },
        { name: 'Jenkins & ArgoCD', level: 'Expert' },
        { name: 'Prometheus & Grafana', level: 'Advanced' },
        { name: 'ELK Stack & Datadog', level: 'Advanced' },
        { name: 'Python & Bash Scripting', level: 'Advanced' },
        { name: 'Kafka & Elasticsearch', level: 'Intermediate' },
        { name: 'GitHub Actions & Spinnaker', level: 'Advanced' },
        { name: 'AWS CloudFormation & Pulumi', level: 'Intermediate' }
      ]
    }
  },
  methods: {
    skillWidth(level) {
      const widths = {
        'Expert': '95%',
        'Advanced': '80%',
        'Intermediate': '65%',
        'Beginner': '40%'
      }
      return widths[level] || '60%'
    }
  }
}
</script>

<style scoped>
.pre-wrap {
  white-space: pre-wrap;
}
</style>
